import React, { useState } from 'react';
import { Plus, Settings, Globe, X } from 'lucide-react';
import { BankFieldsForm, EMPTY_BANK_FORM, CountrySelect, COUNTRIES } from './shared';

/**
 * Per-country bank accounts panel (bank_transfer_per_country plugin)
 * @param {array} accounts - List of per-country bank accounts
 * @param {function} onSave - Callback to save/update (id, formData, countryCodes)
 * @param {function} onDelete - Callback to delete account (id)
 * @param {function} onRemoveCountry - Callback to remove country mapping (accountId, countryCode)
 */
export default function PerCountryBankPanel({ 
  accounts, 
  onSave, 
  onDelete, 
  onRemoveCountry 
}) {
  const [showForm, setShowForm] = useState(false);
  const [pickedCountries, setPickedCountries] = useState([]);
  const [form, setForm] = useState(EMPTY_BANK_FORM);
  const [editId, setEditId] = useState(null);
  const [editCountries, setEditCountries] = useState([]);

  // Flatten all country mappings for display
  const rows = [];
  accounts.forEach((acc) => {
    (acc.mappedCountries || []).forEach((c) => {
      const country = COUNTRIES.find((x) => x.code === c.country_code);
      rows.push({
        ...c,
        account: acc,
        countryName: country?.name || c.country_code,
      });
    });
  });
  rows.sort((a, b) => a.countryName.localeCompare(b.countryName));

  const reset = () => {
    setForm(EMPTY_BANK_FORM);
    setEditId(null);
    setPickedCountries([]);
    setEditCountries([]);
    setShowForm(false);
  };

  const startEdit = (acc) => {
    setForm({
      accountHolder: acc.account_holder,
      bankName: acc.bank_name,
      accountNumber: acc.account_number,
      routingNumber: acc.routing_number || '',
      currency: acc.currency || 'USD',
      isDefault: false,
    });
    setEditId(acc.id);
    setEditCountries((acc.mappedCountries || []).map((c) => c.country_code));
    setShowForm(true);
  };

  const handleSubmit = async () => {
    const countries = editId ? editCountries : pickedCountries;
    await onSave(editId, form, countries);
    reset();
  };

  const uniqueAccounts = accounts.filter((a) => (a.mappedCountries || []).length > 0);

  return (
    <div className="space-y-2">
      {rows.length > 0 && (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div
              key={`${r.country_code}-${r.account.id}`}
              className="flex items-center gap-3 px-3 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg"
            >
              <Globe className="w-4 h-4 text-[var(--text-tertiary)] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[var(--text-primary)]">
                  {r.countryName}{' '}
                  <span className="text-[var(--text-tertiary)] font-mono font-normal">
                    ({r.country_code})
                  </span>
                </p>
                <p className="text-xs text-[var(--text-secondary)]">
                  {r.account.bank_name} · {r.account.currency} · ••••
                  {String(r.account.account_number).slice(-4)}
                </p>
              </div>
              <button
                onClick={() => onRemoveCountry(r.account.id, r.country_code)}
                className="p-1 rounded text-[var(--text-tertiary)] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-smooth"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {uniqueAccounts.length > 0 && !showForm && (
        <div className="border-t border-[var(--border-color)] pt-2 mt-1">
          <p className="text-xs text-[var(--text-secondary)] mb-1.5">
            Edit existing account
          </p>
          <div className="flex flex-wrap gap-1.5">
            {uniqueAccounts.map((acc) => (
              <button
                key={acc.id}
                type="button"
                onClick={() => startEdit(acc)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[var(--border-color)] text-xs text-[var(--text-secondary)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] transition-smooth"
              >
                <Settings className="w-3 h-3" />
                {acc.bank_name} ••••{String(acc.account_number).slice(-4)}
              </button>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="border border-[var(--accent-primary)] rounded-lg p-3 bg-[var(--accent-primary)]/5 space-y-2">
          <p className="text-xs font-semibold text-[var(--text-primary)]">
            {editId ? 'Edit' : 'New'} Per-Country Bank Account
          </p>

          <div>
            <p className="text-xs text-[var(--text-secondary)] mb-1">
              {editId ? 'Mapped countries' : 'Pick country(ies)'}
            </p>
            <div className="flex flex-wrap gap-1 mb-1.5">
              {(editId ? editCountries : pickedCountries).map((cc) => {
                const name = COUNTRIES.find((c) => c.code === cc)?.name || cc;
                return (
                  <span
                    key={cc}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--accent-primary)]/10 text-xs text-[var(--accent-primary)]"
                  >
                    {name} <span className="font-mono">({cc})</span>
                    <button
                      type="button"
                      onClick={() => {
                        if (editId) {
                          setEditCountries((prev) => prev.filter((c) => c !== cc));
                        } else {
                          setPickedCountries((prev) => prev.filter((c) => c !== cc));
                        }
                      }}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                );
              })}
            </div>
            <CountrySelect
              value={editId ? editCountries : pickedCountries}
              onChange={(val) => {
                if (editId) setEditCountries(val);
                else setPickedCountries(val);
              }}
              multiple
              placeholder="Search and add countries…"
            />
          </div>

          <BankFieldsForm form={form} setForm={setForm} showDefault={false} />
          
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSubmit}
              disabled={(editId ? editCountries : pickedCountries).length === 0}
              className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-dark)] text-xs disabled:opacity-40 transition-smooth"
            >
              {editId ? 'Save' : 'Add'}
            </button>
            <button
              onClick={reset}
              className="px-4 py-2 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] text-xs text-[var(--text-primary)] transition-smooth"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 text-xs text-[var(--accent-primary)] hover:text-[var(--accent-primary-dark)] transition-smooth"
        >
          <Plus className="w-3.5 h-3.5" />
          Add country → bank mapping
        </button>
      )}
    </div>
  );
}